import { useEffect, useState } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import axios from "axios";
import { BaseUrlApi, ErrorMessage } from "../../../lib/api";
import { toast } from "sonner";
import LoaderPage from "../../../components/LoaderPage";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { ArrowUpDown, CheckCircle, Eye, Info, XCircle } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { DataTable } from "../../../components/DataTable";
import { DateFormate } from "../../../lib/utils";
import DrawerEditLesson from "./DrawerEditLesson";
import DialogDeleteLesson from "./DialogDeleteLesson";
import { useAuth } from "../../../context/AuthContext";
import { LessonTeacherData } from "../../../lib/languages";

export default function AllLessons() {
  const { language } = useLanguage();
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const t = LessonTeacherData[language] || LessonTeacherData["en"];

  // Lessons
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [lessons, setLessons] = useState([]);

  const getAllLessons = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/group/${id}/lessons`);
      setLessons(data.data);
    } catch (error) {
      toast(t.toastError, {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingLessons(false);
    }
  };

  useEffect(() => {
    setLoadingLessons(true);
    getAllLessons();
  }, []);

  if (loadingLessons) {
    return <LoaderPage title={t.loadingLessonsTitle} />;
  }

  // Get status text and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 0:
        return {
          text: t.inactiveStatus,
          color: "text-red-500 bg-red-50",
          icon: XCircle,
        };
      case 1:
        return {
          text: t.activeStatus,
          color: "text-green-500 bg-green-50",
          icon: CheckCircle,
        };
      default:
        return {
          text: t.unknownStatus,
          color: "text-gray-500 bg-gray-50",
          icon: Info,
        };
    }
  };

  const columns = [
    {
      accessorKey: `title.en`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.columnTitleEn}
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const title = row.original.title;
        return title?.en || "---";
      },
    },
    {
      accessorKey: `title.ar`,
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.columnTitleAr}
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const title = row.original.title;
        return title?.ar || "---";
      },
    },
    {
      accessorKey: "group.name",
      header: t.columnGroup,
    },
    {
      accessorKey: "created_at",
      header: t.columnCreated,
      cell: ({ row }) => {
        const created_at = row.getValue("created_at");
        return DateFormate(created_at);
      },
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="p-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          {t.columnStatus}
          <ArrowUpDown className="h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const status = row.getValue("status");
        const statusInfo = getStatusInfo(status);
        const StatusIcon = statusInfo.icon;
        return (
          <Badge variant="outline" className={statusInfo.color}>
            <StatusIcon size={12} className="mx-1" />
            {statusInfo.text}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: t.columnActions,
      cell: ({ row }) => {
        const item = row.original;
        const isAdmin = user?.role === "admin";
        return (
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              className="rounded-xl h-9"
              onClick={() =>
                navigate(
                  isAdmin
                    ? `/admin/lesson-view/${item.id}`
                    : `/teacher/lesson-view/${item.id}`,
                  {
                    state: {
                      lessonData: item,
                      courseData: item.group.level.course,
                    },
                  }
                )
              }
            >
              <Eye />
            </Button>
            <DrawerEditLesson
              id={id}
              lessonData={item}
              getAllLessons={getAllLessons}
            />
            <DialogDeleteLesson
              id={id}
              lessonData={item}
              getAllLessons={getAllLessons}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <div>
        <div className="text-2xl font-bold">{t.lessonsHeading}</div>
        <DataTable
          columns={columns}
          data={lessons}
          title={t.lessonsByTitle}
          filterNames={["title_ar", "title_en"]}
        />
      </div>
    </div>
  );
}
